/**
 * ログイン中の利用者（ロール・権限つき）。
 *
 * 権限は**ボタンの出し分けにだけ**使う（UX M-04 の方針として確定: 権限の無い操作は
 * 画面側で隠す。サーバ側の 403 はそのまま最後の防御として残る。判定そのものは
 * 常にサーバ側の guard が行うので、ここでの出し分けが仮に古くても実害は無い）。
 * 1度取ったらモジュール内で共有し、ロール割当の変更後は `refreshMe()` で取り直す。
 */
import { useEffect, useState } from "react";
import { api } from "./api.js";

export interface Me {
  userId: string; displayName: string; email: string;
  departmentId: string | null;
  totpEnabled: boolean; passwordChangedAt: string | null;
  /** ローカル認証の資格情報を持つか（パスワードの有無。TOTP ゲートの判定には使わない＝M-1）。 */
  hasLocalCredential: boolean;
  /** このセッションのログイン方法（M-1）。OIDC でログインしたセッションは TOTP 強制の対象外。 */
  sessionAuthMethod: "LOCAL" | "OIDC";
  roles: string[]; permissions: string[];
}

let cache: Me | null = null;
let inflight: Promise<Me> | null = null;
const listeners = new Set<(m: Me | null) => void>();

export function refreshMe(): Promise<Me> {
  // L-12: 失敗時に inflight を null に戻さないと、以後 `!cache && !inflight` に
  // 当たらなくなり（inflight は reject 済みの Promise のまま残る）、
  // 画面を再読み込みするまで取り直さない。
  const p = api.get<{ user: Me }>("/auth/me").then((r) => {
    cache = r.user; inflight = null;
    for (const l of listeners) l(cache);
    return cache;
  }).catch((err) => { inflight = null; throw err; });
  inflight = p;
  return p;
}

/**
 * 利用者ごとのモジュール内キャッシュ（検索の候補・台帳の行など）を消す関数の登録先（M2）。
 * clearMe()（ログアウト・セッション切れ）でまとめて呼ぶ。同じタブで次にログインした人に
 * 前の人が読んだ内容を見せないため。
 */
const userClearers = new Set<() => void>();
export function onUserCleared(fn: () => void): void {
  userClearers.add(fn);
}

/** ログアウト・セッション切れのときに呼ぶ。前の利用者の権限と読んだ内容が次の利用者の画面に残らないように。 */
export function clearMe(): void {
  cache = null; inflight = null;
  for (const fn of userClearers) {
    try { fn(); } catch { /* 1つ失敗しても残りは消す */ }
  }
  for (const l of listeners) l(null);
}

export function useMe(): Me | null {
  const [me, setMe] = useState<Me | null>(cache);
  useEffect(() => {
    listeners.add(setMe);
    if (!cache && !inflight) void refreshMe().catch(() => { /* 未認証 */ });
    return () => { listeners.delete(setMe); };
  }, []);
  return me;
}

export function can(me: Me | null, perm: string): boolean {
  return !!me && me.permissions.includes(perm);
}
